import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import { fetchAdminFaxes } from '@/lib/apiClient';
import { adminFaxKeys } from '@/lib/faxLinking';
import { cappedRefetchInterval } from '@/lib/referralPolling';

const COUNT_POLL_INTERVAL_MS = 60_000;
// Bounded poll (per CODING_STANDARDS.md): stop after this window so an
// abandoned admin tab doesn't fire COUNT queries forever. Returning to the tab
// and every fax link/unlink/delete/restore still refetch it.
const COUNT_POLL_CAP_MS = 30 * 60_000;

/**
 * Number of inbound faxes not yet linked to a record, for the sidebar Inbox
 * badge. Reuses the paginated admin-faxes endpoint with `limit: 1` and reads
 * its `totalItems`, so the badge counts the same rows as the inbox's Unlinked
 * filter.
 */
export function useUnlinkedFaxCount(options?: { enabled?: boolean }) {
  const startedAt = useRef<number | null>(null);
  const enabled = options?.enabled ?? true;
  return useQuery({
    queryKey: adminFaxKeys.unlinkedCount(),
    queryFn: () =>
      fetchAdminFaxes('INBOUND', { page: 1, limit: 1, linkStatus: 'UNLINKED' }),
    select: data => data.totalItems,
    enabled,
    refetchInterval: () =>
      cappedRefetchInterval(
        enabled,
        startedAt,
        COUNT_POLL_INTERVAL_MS,
        COUNT_POLL_CAP_MS
      ),
  });
}
